<nf-grid-wrapper [hideSearch]="true" [hideClearAll]="true">
  <nf-grid-wrapper-header-left>
    <div class="wb-left">
      <span class="wb-role">{{ roleLabel }}</span>
      <div ngbDropdown class="wb-source-picker">
        <button
          type="button"
          ngbDropdownToggle
          class="wb-source-toggle"
          [attr.aria-label]="roleLabel + ' source'"
        >
          <span class="wb-source">
            <span class="wb-source-name">{{ source?.label }}</span>
            <span class="wb-source-tag" [style.color]="source?.tagColor">{{
              source?.tag
            }}</span>
          </span>
          <i class="mdi mdi-chevron-down wb-source-caret"></i>
        </button>
        <div ngbDropdownMenu class="wb-source-menu">
          <h6 class="dropdown-header">{{ roleLabel }} Source</h6>
          <!-- A source held back for now stays listed but disabled, so the pairing the workbench
               is built for is still discoverable. -->
          <button
            *ngFor="let option of sourceOptions"
            type="button"
            ngbDropdownItem
            class="wb-source-option"
            [class.wb-source-current]="option.key === sourceKey"
            [disabled]="option.disabled"
            (click)="onSourcePicked(option.key)"
          >
            <span class="wb-source">
              <span class="wb-source-name">{{ option.label }}</span>
              <span
                class="wb-source-tag wb-source-tag-sm"
                [style.color]="option.tagColor"
                >{{ option.tag }}</span
              >
            </span>
            <i
              class="mdi mdi-check wb-source-check"
              *ngIf="option.key === sourceKey"
            ></i>
          </button>
        </div>
      </div>
    </div>
  </nf-grid-wrapper-header-left>

  <ng-grid-wrapper-header-right class="d-flex" *ngIf="source?.hasStatusFilter">
    <div id="status-quickfilter" class="d-flex mr-2">
      <nf-checkbox
        class="small"
        label="Unmatched"
        [formControl]="includeUnmatchedFormControl"
      ></nf-checkbox>
      <!-- Disabled, with the reason as a tooltip, ONLY on a catalog that has not yet added `IGNORED`
           to matchStatus (PVAL-26415) — a live checkbox that silently filtered nothing would be worse
           than a visibly unavailable one. Enabled and fully functional once the catalog supports it. -->
      <nf-checkbox
        class="small"
        label="Ignored"
        [formControl]="includeIgnoredFormControl"
        [ngbTooltip]="ignoredChipDisabledReason"
        [disableTooltip]="!ignoredChipDisabledReason"
        container="body"
      ></nf-checkbox>
      <nf-checkbox
        class="small"
        label="Matched"
        [formControl]="includeMatchedFormControl"
      ></nf-checkbox>
    </div>
  </ng-grid-wrapper-header-right>

  <ng-grid-wrapper-header-right class="d-flex" *ngIf="hasQuickFilters">
    <div id="quickfilter-container" class="d-flex mr-2">
      <!-- Clearwater's only quick filter. "Breaks Only" used to sit beside it, but there was never
           any break data behind it (the adapter hardcoded zero counts on every row), so it emptied
           the grid instead of filtering it — removed with the Breaks column itself. -->
      <nf-checkbox
        class="small"
        label="Differences Only"
        [formControl]="differencesOnlyToggle"
      ></nf-checkbox>
    </div>
  </ng-grid-wrapper-header-right>

  <ng-grid-wrapper-header-right>
    <!-- Quick Search is DISABLED, with a visible reason, on the server-side sources. A client-side
         quick filter can only match the rows currently loaded, so leaving it enabled would present a
         rolling window's matches as the whole result set — a wrong answer that looks like a right
         one. Clearwater keeps it, because its grid really does hold every row. -->
    <nf-form-control class="d-flex mr-2" compact for="quickSearch">
      <input
        type="text"
        class="form-control"
        [formControl]="quickSearch"
        [attr.disabled]="quickSearchDisabledReason ? '' : null"
        [ngbTooltip]="quickSearchDisabledReason"
        [disableTooltip]="!quickSearchDisabledReason"
        container="body"
        placement="bottom"
        [placeholder]="
          quickSearchDisabledReason ? 'Search unavailable' : 'Search'
        "
        data-cy="quick-search"
      />
    </nf-form-control>
  </ng-grid-wrapper-header-right>

  <ng-grid-wrapper-header-right>
    <div ngbDropdown placement="bottom-right">
      <button
        ngbDropdownToggle
        nf-icon-button
        class="workbench-actions-button"
        ngbTooltip="Position Actions"
        placement="bottom-right"
        icon="dots-vertical"
        style="margin-left: 8px"
      >
        Position Actions
      </button>
      <div
        ngbDropdownMenu
        aria-labelledby="dropdown"
        class="dropdown-menu dropdown-menu-right"
      >
        <button
          type="button"
          class="dropdown-item btn btn-link"
          (click)="exportToExcel()"
        >
          Export to Excel
        </button>
      </div>
    </div>
  </ng-grid-wrapper-header-right>

  <ng-grid-wrapper-header-right>
    <button
      nf-icon-button="secondary"
      ngbTooltip="Filters Modal"
      placement="bottom-right"
      icon="filter"
      (click)="openFilters()"
      data-cy="advancedFiltersBtn"
    >
      Open Filters Modal
    </button>
    <hs-applied-filter-counts
      [filterCount]="filterCount"
    ></hs-applied-filter-counts>
  </ng-grid-wrapper-header-right>

  <ng-grid-wrapper-header-right>
    <button
      id="clear-all-filters-button"
      nf-icon-button="secondary"
      (click)="clearAll()"
      icon="filter-remove"
      ngbTooltip="Clear All Filters"
      [disabled]="!clearAllFiltersEnabled"
      placement="bottom-right"
    >
      Clear all filters
    </button>
  </ng-grid-wrapper-header-right>

  <nf-grid-wrapper-content *ngIf="gridOptions">
    <div class="wb-grid-area">
      <!-- Terminal states for the catalog fetch. These sit above the grid: on error it falls back
           to the computed columns, and an empty catalog is a legitimate answer. The two error kinds
           read differently (work item 13): a 404 is definitive (RPS has no Enfusion metadata for
           this account's CW client) and offers no retry, while a 5xx is transient and offers a
           Retry. Both surface the errorId so a bug report can be keyed on it. -->
      <div
        *ngIf="!columnsLoading && columnsErrorKind === 'not-retryable'"
        class="wb-columns-status wb-columns-error"
        data-cy="columns-error-not-retryable"
      >
        <i class="mdi mdi-alert-circle"></i>
        <span>
          Position fields are not available for this account yet — showing the
          default columns only.
          <span *ngIf="columnsErrorId" class="wb-columns-error-id"
            >Error ID: {{ columnsErrorId }}</span
          >
        </span>
      </div>
      <div
        *ngIf="!columnsLoading && columnsErrorKind === 'retryable'"
        class="wb-columns-status wb-columns-error"
        data-cy="columns-error-retryable"
      >
        <i class="mdi mdi-alert-circle"></i>
        <span>
          Couldn't load the column catalog — showing the default columns only.
          <span *ngIf="columnsErrorId" class="wb-columns-error-id"
            >Error ID: {{ columnsErrorId }}</span
          >
        </span>
        <button
          type="button"
          class="btn btn-link btn-sm wb-columns-retry"
          data-cy="columns-retry"
          (click)="retryFetch()"
        >
          Retry
        </button>
      </div>
      <div
        *ngIf="!columnsLoading && !columnsError && columnsEmpty"
        class="wb-columns-status wb-columns-empty"
        data-cy="columns-empty"
      >
        <i class="mdi mdi-information-outline"></i>
        No columns are catalogued for this source yet.
      </div>

      <!-- A failed row query otherwise leaves the grid empty and silent: ag-Grid's "No data"
           overlay is the same thing it shows for a legitimately empty page, so the failure was
           indistinguishable from "this account has no positions". Styled as the same "Cache Load
           Error" card `hs-cache-load-listener` uses for a session-cache failure, so a query failure
           reads with the same visual weight/severity as every other "something broke" state in
           Helios, rather than a bespoke inline strip. Three kinds read differently inside it because
           they are three different situations, and only one of them is "something broke":

            - INVALID_FIELD_TYPE (400): a comparison type Postgres could not apply. Names the
              column(s) and the offending value, and offers Reset to Text — the ONLY kind with a
              targeted action.
            - session expired (410): nothing is broken and a Retry cannot help, so it is presented
              as a re-open instruction rather than as a 5xx and never as a zero-row result.
            - anything else: transient, so Retry re-runs only the rows (the catalog resolved). -->
      <hs-panel-card
        *ngIf="!columnsLoading && queryFailure?.kind === 'invalid-field-type'"
        header="Cache Load Error"
        icon="mdi-alert-circle"
        iconClass="text-danger"
        data-cy="rows-error-invalid-field-type"
      >
        <p>
          {{ invalidFieldTypeMessage }}
          <span *ngIf="queryFailure?.errorId" class="wb-columns-error-id"
            >Error ID: {{ queryFailure?.errorId }}</span
          >
        </p>
        <button
          type="button"
          class="btn btn-link btn-sm wb-columns-retry"
          data-cy="rows-reset-type"
          (click)="resetTypesToText()"
        >
          Reset type to Text
        </button>
      </hs-panel-card>
      <hs-panel-card
        *ngIf="!columnsLoading && queryFailure?.kind === 'session-expired'"
        header="Cache Load Error"
        icon="mdi-clock-alert-outline"
        iconClass="text-danger"
        data-cy="rows-error-session-expired"
      >
        <p>
          Your positions session has expired. Reload the panel to start a new
          one.
        </p>
      </hs-panel-card>
      <hs-panel-card
        *ngIf="!columnsLoading && queryFailure?.kind === 'unknown'"
        header="Cache Load Error"
        icon="mdi-alert-circle"
        iconClass="text-danger"
        data-cy="rows-error"
      >
        <p>
          Couldn't load positions for this source.
          <span *ngIf="queryFailure?.errorId" class="wb-columns-error-id"
            >Error ID: {{ queryFailure?.errorId }}</span
          >
        </p>
        <button
          type="button"
          class="btn btn-link btn-sm wb-columns-retry"
          data-cy="rows-retry"
          (click)="retryRows()"
        >
          Retry
        </button>
      </hs-panel-card>

      <nf-table-layout
        [hideTitle]="true"
        [isSectionTitle]="true"
        [gridOptions]="gridOptions"
        [modules]="gridModules"
        hideSearch
        hideClearAll
        [autoResizeGrid]="false"
      >
      </nf-table-layout>

      <!-- While the catalog is in flight, mask the whole grid area — it is being rebuilt around a
           new set of columns.

           The spinner is the global `spinner-border` class, matching Transactions 2.0, rather than
           `hs-loader`. That component's host sits at `opacity: 0` until an `active` class is added
           on top of `[loading]`, so `[loading]="true"` alone renders an opaque overlay with a fully
           transparent spinner inside it — a blank grid. Its `nfLoader` directive is not an option
           either: it swaps the element out, which would rebuild the ag-Grid instance (and its store
           and filter-modal registrations) on every catalog fetch. -->
      <div
        *ngIf="columnsLoading"
        class="wb-grid-loading"
        data-cy="columns-loading"
        role="status"
        aria-live="polite"
      >
        <span class="spinner-border" aria-hidden="true"></span>
        <span class="wb-grid-loading-label">Loading...</span>
      </div>

      <!-- The row query deliberately has no mask of its own. It only fires once the catalog above
           has resolved, so by then the columns exist and the headers should stay on screen with a
           spinner over the body only — that is ag-Grid's own loading overlay, raised in
           `fetchRows`. Masking the whole area here made a slow query look like a grid that had
           never loaded. -->
    </div>
  </nf-grid-wrapper-content>
</nf-grid-wrapper>
